/**
 * Preserve tagguid-ownership URLs across Okta OAuth redirect.
 *
 * ─── Problem ─────────────────────────────────────────────────────────
 * When a user clicks a manager-approval or nominee-accept email link
 * from Chrome (or any browser where the Nike SSO cookie is treated as
 * third-party at the time of navigation), the flow is:
 *
 *   1. Browser opens  cloudred.nike.com/ownership-approval/{id}/{token}
 *   2. React mounts, `initializeUserAuth()` detects no session
 *   3. Okta OAuth redirect fires: browser navigates to nike.okta.com
 *   4. User signs in on Okta
 *   5. Okta redirects back to  cloudred.nike.com/  (root — path dropped)
 *   6. `useRoutes` in App.js matches nothing → `<NotFoundPage />` renders
 *      → user sees "Oops! We couldn't find the page you were looking for."
 *
 * Reload "fixes" it because the second attempt already has the SSO
 * cookie set, so no redirect happens and the URL stays intact. Safari
 * happens to preserve the SSO cookie across the redirect via ITP-friendly
 * handling, so Safari users don't hit the bug.
 *
 * ─── Solution ────────────────────────────────────────────────────────
 * This module runs at MODULE LOAD TIME — synchronously, before any React
 * component renders and before `useRoutes` evaluates the URL. It:
 *
 *   • On the email-link first-load (URL matches an ownership route):
 *     stash the full path+search in sessionStorage so we can restore it
 *     if an Okta redirect strips it later.
 *
 *   • On the post-Okta redirect load (URL is now "/"): read the stashed
 *     path and use `history.replaceState` to put it back. The upcoming
 *     `useRoutes` call reads the restored `window.location` and matches
 *     the ownership route correctly, so the target page renders instead
 *     of NotFoundPage.
 *
 * sessionStorage is per-tab-per-origin, and it IS preserved across the
 * cloudred.nike.com → nike.okta.com → cloudred.nike.com round-trip in
 * the same tab. Once we successfully restore, the stash is cleared so a
 * later manual visit to "/" (the homepage) doesn't get bounced.
 *
 * ─── Scope ───────────────────────────────────────────────────────────
 * Strictly ownership-approval and ownership-nominee URLs. Zero effect
 * on any other route. Zero changes to shared files (App.js, routes.js,
 * LoginRequired.js, index.html) — this file lives inside the tagguid-
 * ownership feature and is side-effect-imported by the two ownership
 * pages, which are statically imported by routes.js.
 *
 * If we ever move to code-splitting (React.lazy) for the ownership
 * pages, this needs to move to a module that's still in the main bundle
 * — otherwise the stash-and-restore runs too late.
 */

const STORAGE_KEY = "cloudred:tagguid_ownership_intended_url";
const OWNERSHIP_PATH_RE = /^\/(ownership-approval|ownership-nominee)\//;
const ROOT_PATH_RE = /^\/(cloudred\/?)?$/;

if (typeof window !== "undefined" && window.location && window.sessionStorage) {
  try {
    const pathname = window.location.pathname;
    const search = window.location.search || "";

    if (OWNERSHIP_PATH_RE.test(pathname)) {
      // Case 1: browser is currently on an ownership URL. Stash it so
      // we can survive any Okta redirect that lands us on "/".
      window.sessionStorage.setItem(STORAGE_KEY, pathname + search);
    } else if (ROOT_PATH_RE.test(pathname)) {
      // Case 2: browser is on root — likely a post-Okta landing. If we
      // have a stashed ownership URL, restore it BEFORE useRoutes runs.
      const saved = window.sessionStorage.getItem(STORAGE_KEY);
      if (saved && OWNERSHIP_PATH_RE.test(saved.split("?")[0])) {
        window.sessionStorage.removeItem(STORAGE_KEY);
        // replaceState changes the URL without triggering navigation.
        // App.js's `useRoutes` reads window.location during its next
        // (upcoming) render, so it will see the restored path.
        window.history.replaceState({}, "", saved);
      }
    }
  } catch (err) {
    // sessionStorage can throw in Safari private mode or on quota
    // exceeded. This is a UX enhancement — swallow so we never crash
    // the app for anyone.
    // eslint-disable-next-line no-console
    console.warn("[cloudred] tagguid-ownership URL preservation skipped:", err);
  }
}

// Explicit empty export so bundlers treat this as an ES module even
// though it has no exports (only side effects).
export {};
